"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import publicApiClient, { ApiError } from '../api/public-client';

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  avatar_url?: string;
  auth_provider: 'email' | 'google' | 'facebook';
  email_verified: boolean;
  is_active?: boolean;
  last_login_at?: string;
  login_count?: number;
  created_at: string;
}
interface AuthProviders {
  email: boolean;
  google: boolean;
  facebook: boolean;
}
interface SendOTPResponse {
  success: boolean;
  message: string;
  email?: string;
  expires_in_seconds?: number;
  retryAfter?: number;
}
interface VerifyOTPResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: User;
  is_new_user?: boolean;
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  providers: AuthProviders | null;

  // Auth state for UI
  authEmail: string;
  setAuthEmail: (email: string) => void;
  otpSent: boolean;
  otpExpiresIn: number;
  resendCountdown: number;

  // Actions
  sendOTP: (email: string, purpose?: 'login' | 'registration', name?: string) => Promise<SendOTPResponse>;
  verifyOTP: (email: string, otpCode: string, name?: string) => Promise<VerifyOTPResponse>;
  resendOTP: () => Promise<SendOTPResponse>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  clearError: () => void;
  resetAuthState: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function PublicAuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<AuthProviders | null>(null);

  // OTP flow state
  const [authEmail, setAuthEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(0);
  const isAuthenticated = !!user;

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  // Fetch user on mount if token exists
  useEffect(() => {
    const initAuth = async () => {
      if (publicApiClient.hasToken()) {
        try {
          await fetchUser();
        } catch {
          publicApiClient.removeToken();
        }
      }
      setIsLoading(false);
    };
    initAuth();
    fetchProviders();
  }, []);
  const fetchProviders = async () => {
    try {
      const response = await publicApiClient.get<{
        success: boolean;
        providers: AuthProviders;
      }>('/auth/providers');
      if (response.success) {
        setProviders(response.providers);
      }
    } catch {
      // Default to email only if fetch fails
      setProviders({
        email: true,
        google: false,
        facebook: false
      });
    }
  };
  const fetchUser = useCallback(async () => {
    try {
      const response_0 = await publicApiClient.get<{
        success: boolean;
        user: User;
      }>('/auth/me');
      if (response_0.success && response_0.user) {
        setUser(response_0.user);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        publicApiClient.removeToken();
        setUser(null);
      }
      throw err;
    }
  }, []);
  const sendOTP = useCallback(async (email: string, purpose: 'login' | 'registration' = 'login', name?: string): Promise<SendOTPResponse> => {
    setError(null);
    setIsLoading(true);
    try {
      const response_1 = await publicApiClient.post<SendOTPResponse>('/auth/send-otp', {
        email,
        purpose,
        ...(name && {
          name
        })
      });
      if (response_1.success) {
        setAuthEmail(email);
        setOtpSent(true);
        setOtpExpiresIn(response_1.expires_in_seconds || 300);
        setResendCountdown(60); // 60 second cooldown for resend
      }
      return response_1;
    } catch (err_0) {
      const message = err_0 instanceof ApiError ? err_0.message : 'Failed to send OTP';
      setError(message);

      // Handle rate limiting
      if (err_0 instanceof ApiError && err_0.status === 429) {
        const data = err_0.data as {
          retryAfter?: number;
        };
        if (data.retryAfter) {
          setResendCountdown(data.retryAfter);
        }
      }
      return {
        success: false,
        message
      };
    } finally {
      setIsLoading(false);
    }
  }, []);
  const verifyOTP = useCallback(async (email_0: string, otpCode: string, name_0?: string): Promise<VerifyOTPResponse> => {
    setError(null);
    setIsLoading(true);
    try {
      const response_2 = await publicApiClient.post<VerifyOTPResponse>('/auth/verify-otp', {
        email: email_0,
        otp_code: otpCode,
        ...(name_0 && {
          name: name_0
        })
      });
      if (response_2.success && response_2.token) {
        publicApiClient.setToken(response_2.token);
        if (response_2.user) {
          setUser(response_2.user);
        }
        // Reset OTP state
        setOtpSent(false);
        setAuthEmail('');
      }
      return response_2;
    } catch (err_1) {
      const message_0 = err_1 instanceof ApiError ? err_1.message : 'Failed to verify OTP';
      setError(message_0);
      return {
        success: false,
        message: message_0
      };
    } finally {
      setIsLoading(false);
    }
  }, []);
  const resendOTP = useCallback(async (): Promise<SendOTPResponse> => {
    if (!authEmail) {
      return {
        success: false,
        message: 'No email address set'
      };
    }
    return sendOTP(authEmail);
  }, [authEmail, sendOTP]);
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await publicApiClient.post('/auth/logout');
    } catch {
      // Ignore logout errors
    } finally {
      publicApiClient.removeToken();
      setUser(null);
      setIsLoading(false);
      resetAuthState();
    }
  }, []);
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  const resetAuthState = useCallback(() => {
    setAuthEmail('');
    setOtpSent(false);
    setOtpExpiresIn(0);
    setResendCountdown(0);
    setError(null);
  }, []);
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    providers,
    authEmail,
    setAuthEmail,
    otpSent,
    otpExpiresIn,
    resendCountdown,
    sendOTP,
    verifyOTP,
    resendOTP,
    logout,
    fetchUser,
    clearError,
    resetAuthState
  };
  return <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>;
}
export function usePublicAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("usePublicAuth must be used within a PublicAuthProvider");
  }
  return context;
}

// Safe hook that doesn't throw if used outside provider
// Returns null if not within PublicAuthProvider
export function usePublicAuthSafe() {
  return useContext(AuthContext);
}